import React from 'react';
import styled from 'styled-components';

import Row from 'components/Common/Row';
import Alert from 'kingpin/Alert';
import Typography from 'kingpin/atoms/Typography';

import useFormatPaymentAmount from './useFormatPaymentAmount';

const BudgetCard = styled.div`
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border-secondary);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const CapPill = styled.div`
  background-color: var(--color-bg-tertiary);
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const Track = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  background-color: var(--color-bg-tertiary);
  overflow: hidden;
  margin: 12px 0px 8px;
`;

const Segment = styled.div<{ $percent: number; $color: string }>`
  width: ${(props) => props.$percent}%;
  background-color: ${(props) => props.$color};
  transition: width 0.3s ease;
`;

const toPercent = (value: number, max: number) =>
  max <= 0 ? 0 : Math.min(100, Math.max(0, (value / max) * 100));

// How much of the period's recognition payment budget is used and left, with
// the amount being added (if any) previewed on the bar.
const PaymentBudget = ({
  policy,
  response,
  pendingAmount,
}: {
  policy: BonusTypes.AdditionalPayments.Policy;
  response: BonusTypes.AdditionalPayments.Response;
  pendingAmount?: number;
}) => {
  const formatAmount = useFormatPaymentAmount();
  const remaining = response.remaining ?? 0;
  const pending = Math.min(pendingAmount ?? 0, remaining);

  return (
    <BudgetCard data-testid={'additional-payments-budget'}>
      <Row spaceBetween style={{ alignItems: 'flex-start', gap: 8 }}>
        <div>
          <Typography.Body type={'Body 12'} color={'var(--color-text-muted)'}>
            {'Left to give this period'}
          </Typography.Body>
          <Typography.Header type={'H2'}>
            {formatAmount(remaining)}
          </Typography.Header>
          {pending > 0 && (
            <Typography.Body
              type={'Body 12'}
              color={'var(--color-status-success)'}
            >
              {`${formatAmount(remaining - pending)} after this payment`}
            </Typography.Body>
          )}
        </div>
        <CapPill>
          <Typography.Body type={'Body 12'}>
            {`Up to ${formatAmount(policy.maxSinglePayment)} per payment`}
          </Typography.Body>
        </CapPill>
      </Row>
      <Track>
        <Segment
          $percent={toPercent(
            response.totalAdditionalPayments,
            policy.maxTotalPayments,
          )}
          $color={'var(--color-info-v2)'}
        />
        <Segment
          $percent={toPercent(pending, policy.maxTotalPayments)}
          $color={'var(--color-status-success)'}
        />
      </Track>
      <Typography.Body type={'Body 12'} color={'var(--color-text-muted)'}>
        {`${formatAmount(response.totalAdditionalPayments)} of ${formatAmount(
          policy.maxTotalPayments,
        )} used`}
      </Typography.Body>
    </BudgetCard>
  );
};

// The disqualified note and the period's payment budget, shared by the add and
// manage views.
const AdditionalPaymentsSummary = ({
  bonusOverview,
  response,
  pendingAmount,
}: {
  bonusOverview: BonusTypes.BonusOverview;
  response: BonusTypes.AdditionalPayments.Response;
  // A valid amount about to be added, previewed on the budget bar.
  pendingAmount?: number;
}) => (
  <>
    {bonusOverview.actualRewardOutput.eligibleForBonus === 'disqualified' && (
      <div style={{ marginBottom: 16 }}>
        <Alert
          type={'Info'}
          body={
            'This driver is disqualified for the period. Additional payments are recorded but add $0 while the driver is disqualified.'
          }
        />
      </div>
    )}
    {response.policy && (
      <PaymentBudget
        policy={response.policy}
        response={response}
        pendingAmount={pendingAmount}
      />
    )}
  </>
);

export default AdditionalPaymentsSummary;
